/**
 * useEventData Hook
 *
 * Custom React hook that encapsulates all data fetching and processing
 * for the EventDetail page.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchEvent,
  fetchEventLifecycle,
  fetchBettingVolume,
  type EventLifecycleResponse,
  type BettingVolumeResponse,
} from '../../../api/client';
import { useUnifiedChartData, type UseUnifiedChartDataResult } from '../../../hooks/useUnifiedChartData';
import {
  processOddsForLineChart,
  processPolymarketPrices,
  calculatePriceGapOverTime,
  processSignalsForChart,
  calculatePolymarket24hChange,
  calculatePriceGap,
  SHARP_BOOK_NAMES,
  SQUARE_BOOK_NAMES,
  type LineChartDataPoint,
  type LiveOdds,
  type PolymarketChartData,
  type PriceGapChartData,
  type SignalChartData,
} from '../utils';
import type { EventDetail, Signal, Opportunity } from '../../../types';

// ============================================
// Types
// ============================================

export interface UseEventDataResult {
  // Query states
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;

  // Raw data
  data: EventDetail | { error: string } | undefined;
  lifecycleData: EventLifecycleResponse | undefined;
  bettingVolumeData: BettingVolumeResponse | null | undefined;

  // Extracted data
  event: EventDetail['event'] | null;
  polymarket: EventDetail['polymarket'] | null;
  signals: Signal[];
  opportunities: Opportunity[];
  oddsHistory: EventDetail['oddsHistory'];
  marketStats: EventDetail['marketStats'];
  currentOddsByMarket: EventDetail['currentOddsByMarket'];
  crossMarketStats: EventDetail['crossMarketStats'];

  // Processed chart data
  lineChartData: LineChartDataPoint[];
  liveOdds: LiveOdds[];
  bookmakersInData: string[];
  polymarketChartData: PolymarketChartData[];
  priceGapChartData: PriceGapChartData[];
  signalChartData: SignalChartData[];
  unifiedChartResult: UseUnifiedChartDataResult;

  // Bookmaker categorization
  availableBookmakers: string[];
  sharpBooks: string[];
  squareBooks: string[];

  // Computed values
  polymarketPrice: number | null;
  polymarketChange24h: number | null;
  lastOddsUpdate: string | null;
  priceGap: number | null;
  bestOpportunity: Opportunity | null;

  // Bookmaker selection state
  selectedBookmakers: Set<string>;
  toggleBookmaker: (bookmaker: string) => void;
}

// ============================================
// Main Hook
// ============================================

export function useEventData(eventId: string | undefined): UseEventDataResult {
  // Bookmaker selection state
  const [selectedBookmakers, setSelectedBookmakers] = useState<Set<string>>(new Set(['all']));

  // ==========================================
  // React Query Hooks
  // ==========================================

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => fetchEvent(eventId!),
    enabled: !!eventId,
    refetchInterval: 60000, // Auto-refresh every 60 seconds
  });

  const { data: lifecycleData } = useQuery({
    queryKey: ['event-lifecycle', eventId],
    queryFn: () => fetchEventLifecycle(eventId!),
    enabled: !!eventId,
    refetchInterval: 30000, // Refresh lifecycle every 30 seconds
  });

  const { data: bettingVolumeData } = useQuery({
    queryKey: ['event-betting-volume', eventId],
    queryFn: () => fetchBettingVolume(eventId!),
    enabled: !!eventId,
    refetchInterval: 60000, // Refresh every 60 seconds
  });

  // ==========================================
  // Extract data safely (may be undefined during loading)
  // ==========================================

  const event = data && !('error' in data) ? data.event : null;
  const polymarket = data && !('error' in data) ? data.polymarket : null;
  const signals = data && !('error' in data) ? data.signals : [];
  const opportunities = data && !('error' in data) ? data.opportunities : [];
  const oddsHistory = data && !('error' in data) ? data.oddsHistory : [];
  const marketStats = data && !('error' in data) ? data.marketStats : [];
  const currentOddsByMarket = data && !('error' in data) ? data.currentOddsByMarket : {};
  const crossMarketStats = data && !('error' in data) ? data.crossMarketStats : null;

  // ==========================================
  // Process odds for LINE chart
  // ==========================================

  const { lineChartData, liveOdds, bookmakersInData } = useMemo(() => {
    if (!event || !oddsHistory.length) {
      return { lineChartData: [], liveOdds: [], bookmakersInData: [] };
    }
    return processOddsForLineChart(
      oddsHistory,
      selectedBookmakers.has('all') ? null : Array.from(selectedBookmakers),
      event.homeTeam,
      event.awayTeam
    );
  }, [oddsHistory, selectedBookmakers, event]);

  // ==========================================
  // Process Polymarket price history for synchronized chart
  // ==========================================

  const polymarketChartData = useMemo(() => {
    if (!polymarket?.priceHistory || polymarket.priceHistory.length === 0) {
      return [];
    }
    return processPolymarketPrices(polymarket.priceHistory, event?.homeTeam, event?.awayTeam);
  }, [polymarket?.priceHistory, event?.homeTeam, event?.awayTeam]);

  // ==========================================
  // Calculate price gap over time (Polymarket vs Odds implied probability)
  // ==========================================

  const priceGapChartData = useMemo(() => {
    if (!polymarket?.priceHistory || !oddsHistory.length) {
      return [];
    }
    return calculatePriceGapOverTime(polymarket.priceHistory, oddsHistory);
  }, [polymarket?.priceHistory, oddsHistory]);

  // ==========================================
  // Process RLM signal strength over time
  // ==========================================

  const signalChartData = useMemo(() => {
    if (!signals.length) {
      return [];
    }
    return processSignalsForChart(signals);
  }, [signals]);

  // ==========================================
  // Unified chart data - combines all data sources for overlay visualization
  // ==========================================

  const unifiedChartResult = useUnifiedChartData({
    event: event
      ? {
          id: event.id,
          homeTeam: event.homeTeam,
          awayTeam: event.awayTeam,
          commenceTime: event.commenceTime,
          sport: event.sport,
        }
      : null,
    oddsHistory: oddsHistory
      .filter((o) => {
        // Filter out Over/Under outcomes for unified chart (h2h only)
        const outcome = o.outcomeName?.toLowerCase() || '';
        return outcome !== 'over' && outcome !== 'under';
      })
      .map((o) => ({
        timestamp: o.timestamp,
        bookmaker: o.bookmaker || 'Unknown',
        outcomeName: o.outcomeName || '',
        price: o.price,
        isSharp: o.isSharp ?? null,
      })),
    polymarketHistory:
      polymarket?.priceHistory
        ?.filter((p) => {
          // Filter to relevant outcomes based on bet type
          const outcome = p.outcome.toLowerCase();
          if (polymarket?.betType === 'moneyline') {
            // For moneyline, exclude Over/Under outcomes
            return outcome !== 'over' && outcome !== 'under';
          }
          if (polymarket?.betType === 'total') {
            // For totals, only include Over/Under
            return outcome === 'over' || outcome === 'under';
          }
          return true; // Include all for other bet types
        })
        .map((p) => ({
          timestamp: p.timestamp,
          outcome: p.outcome,
          midPrice: p.midPrice,
          bidPrice: p.bidPrice,
          askPrice: p.askPrice,
        })) || [],
    signals: signals.map((s) => ({
      timestamp: s.detectedAt,
      chainValue: s.strength * 100,
      direction: s.direction as 'home' | 'away' | 'over' | 'under',
      signalType: s.signalType,
    })),
    maxPoints: 300,
    enabled: !!event,
  });

  // ==========================================
  // Extract unique bookmakers from odds history
  // ==========================================

  const availableBookmakers = useMemo(() => {
    return Array.from(new Set(oddsHistory.map((o) => o.bookmaker || 'Unknown')));
  }, [oddsHistory]);

  const sharpBooks = useMemo(() => {
    return availableBookmakers.filter((b) =>
      SHARP_BOOK_NAMES.some((sharp) => b.toLowerCase().includes(sharp))
    );
  }, [availableBookmakers]);

  const squareBooks = useMemo(() => {
    return availableBookmakers.filter((b) =>
      SQUARE_BOOK_NAMES.some((square) => b.toLowerCase().includes(square))
    );
  }, [availableBookmakers]);

  // ==========================================
  // Calculate KPIs
  // ==========================================

  const polymarketPrice = polymarket?.currentPrice || null;
  const polymarketChange24h = useMemo(
    () => calculatePolymarket24hChange(polymarket?.priceHistory),
    [polymarket?.priceHistory]
  );
  const lastOddsUpdate = oddsHistory.length > 0 ? oddsHistory[0].timestamp : null;
  const priceGap = useMemo(
    () => calculatePriceGap(polymarketPrice, oddsHistory),
    [polymarketPrice, oddsHistory]
  );

  // ==========================================
  // Find best opportunity
  // ==========================================

  const bestOpportunity = useMemo(() => {
    if (opportunities.length === 0) return null;
    return opportunities.reduce(
      (best, curr) => (curr.adjustedEdge > best.adjustedEdge ? curr : best),
      opportunities[0]
    );
  }, [opportunities]);

  // ==========================================
  // Toggle bookmaker selection
  // ==========================================

  const toggleBookmaker = (bookmaker: string) => {
    const newSelection = new Set(selectedBookmakers);
    if (bookmaker === 'all') {
      newSelection.clear();
      newSelection.add('all');
    } else {
      newSelection.delete('all');
      if (newSelection.has(bookmaker)) {
        newSelection.delete(bookmaker);
      } else {
        newSelection.add(bookmaker);
      }
      if (newSelection.size === 0) {
        newSelection.add('all');
      }
    }
    setSelectedBookmakers(newSelection);
  };

  // ==========================================
  // Return all data
  // ==========================================

  return {
    // Query states
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,

    // Raw data
    data,
    lifecycleData,
    bettingVolumeData,

    // Extracted data
    event,
    polymarket,
    signals,
    opportunities,
    oddsHistory,
    marketStats,
    currentOddsByMarket,
    crossMarketStats,

    // Processed chart data
    lineChartData,
    liveOdds,
    bookmakersInData,
    polymarketChartData,
    priceGapChartData,
    signalChartData,
    unifiedChartResult,

    // Bookmaker categorization
    availableBookmakers,
    sharpBooks,
    squareBooks,

    // Computed values
    polymarketPrice,
    polymarketChange24h,
    lastOddsUpdate,
    priceGap,
    bestOpportunity,

    // Bookmaker selection state
    selectedBookmakers,
    toggleBookmaker,
  };
}

export default useEventData;
